import { m, useReducedMotion } from 'framer-motion';
import { Layout } from '../components/layout';
import { ContactForm } from '../components/ContactForm';
import { SinceFoundation } from '../components/SinceFoundation';
import { SloganLine } from '../components/SloganLine';

// ---------------------------------------------------------------------------
// Animation variants
// ---------------------------------------------------------------------------

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1 },
  },
};

const fadeInUp = {
  hidden: { opacity: 0, y: 24 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.5, ease: [0.25, 0.1, 0.25, 1] as const },
  },
};

// Instant variant used when the user prefers reduced motion
const noMotion = {
  hidden: { opacity: 1, y: 0 },
  visible: { opacity: 1, y: 0 },
};

const noMotionContainer = {
  hidden: { opacity: 1 },
  visible: { opacity: 1 },
};

// ---------------------------------------------------------------------------
// Section divider / label helpers
// ---------------------------------------------------------------------------

function SectionDivider() {
  return <div className="w-16 h-px bg-white/20 mb-8" />;
}

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <p className="text-[11px] font-en font-bold uppercase tracking-widest text-white/30 mb-6">
      {children}
    </p>
  );
}

// ---------------------------------------------------------------------------
// Business area data
// ---------------------------------------------------------------------------

const businessAreas = [
  {
    number: '01',
    title: 'SPATIAL MEDIA',
    subtitle: '공간미디어 콘텐츠',
    description:
      'Projection mapping, LED media facades, immersive installations for public and commercial spaces.',
  },
  {
    number: '02',
    title: 'EXPERIENCE DESIGN',
    subtitle: '경험 디자인',
    description:
      'Exhibition design, interactive environments, spatial narratives that transform how people engage with physical space.',
  },
  {
    number: '03',
    title: 'DIGITAL CREATIVE',
    subtitle: '디지털 크리에이티브',
    description:
      'Brand identity, motion graphics, web design, and digital content that extends spatial experiences into the digital realm.',
  },
];

// ---------------------------------------------------------------------------
// Values data
// ---------------------------------------------------------------------------

const values = [
  {
    number: '01',
    title: 'LIGHT',
    en: 'Light as our origin and medium. We see, shape, and breathe light into every space.',
    kr: '빛을 매체로. 모든 공간에 빛을 불어넣습니다.',
  },
  {
    number: '02',
    title: 'SPACE',
    en: 'Space as our canvas. We transform physical environments into living narratives.',
    kr: '공간을 캔버스로. 물리적 환경을 살아있는 내러티브로 변환합니다.',
  },
  {
    number: '03',
    title: 'PEOPLE',
    en: 'People as our purpose. Every experience we create exists to move, connect, and inspire.',
    kr: '사람이 목적. 모든 경험은 감동과 연결, 영감을 위해 존재합니다.',
  },
];

// ---------------------------------------------------------------------------
// Journey milestones
// ---------------------------------------------------------------------------

const milestones = [
  { year: '2018.05', label: 'Founded in Busan, Korea', isCurrent: false },
  { year: '2019', label: 'First spatial media project', isCurrent: false },
  { year: '2024', label: 'Digital creative expansion', isCurrent: false },
];

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export default function AboutPage() {
  const shouldReduceMotion = useReducedMotion();

  const container = shouldReduceMotion ? noMotionContainer : staggerContainer;
  const item = shouldReduceMotion ? noMotion : fadeInUp;

  const viewportConfig = { once: true, margin: '-80px' as const };

  return (
    <Layout>
      {/* ================================================================
          Section 1 — HERO / SLOGAN
          ================================================================ */}
      <section
        id="hero"
        className="relative flex flex-col items-center justify-center min-h-screen bg-black px-6 md:px-10 overflow-hidden"
        aria-label="Brand slogan"
      >
        <m.div
          initial="hidden"
          animate="visible"
          variants={container}
          className="w-full max-w-5xl"
        >
          {/* FROM LIGHT —— TO CREATIVE */}
          <m.div variants={item}>
            <SloganLine size="lg" />
          </m.div>

          {/* Philosophy statement */}
          <m.p
            variants={item}
            className="font-kr text-xl md:text-2xl text-white/50 leading-relaxed max-w-md mt-10 md:mt-14"
          >
            빛은 공간을 바꾸고, 공간은 사람을 바꿉니다.
          </m.p>
        </m.div>

        {/* Scroll indicator */}
        <div
          className="absolute bottom-10 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2"
          aria-hidden="true"
        >
          <span className="text-[10px] font-en font-bold uppercase tracking-widest text-white/25">
            Scroll
          </span>
          <svg
            className="w-4 h-4 text-white/25 animate-bounce"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            aria-hidden="true"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1.5}
              d="M19 9l-7 7-7-7"
            />
          </svg>
        </div>
      </section>

      {/* ================================================================
          Section 2 — PHILOSOPHY
          ================================================================ */}
      <section
        id="philosophy"
        className="relative min-h-screen flex items-center px-6 md:px-10 py-24 md:py-32"
        aria-labelledby="philosophy-heading"
      >
        <div className="edge-line is-animated absolute top-0 left-0 right-0" aria-hidden="true" />
        <m.div
          initial="hidden"
          whileInView="visible"
          viewport={viewportConfig}
          variants={container}
          className="max-w-3xl"
        >
          <m.div variants={item}>
            <SectionLabel>Philosophy</SectionLabel>
            <SectionDivider />
          </m.div>

          {/* EN quote */}
          <m.blockquote
            variants={item}
            className="font-en font-bold text-2xl md:text-4xl text-white leading-snug mb-8"
          >
            <span className="italic text-white/80">"</span>We believe{' '}
            <span className="border-b-2 border-highlight">
              light transforms space
            </span>
            ,<br className="hidden md:block" /> and{' '}
            <span className="border-b-2 border-highlight">
              space transforms people
            </span>
            .<span className="italic text-white/80">"</span>
          </m.blockquote>

          {/* KR translation */}
          <m.div variants={item} className="space-y-3">
            <p className="font-kr text-base text-white/50 leading-relaxed">
              빛은 공간을 바꾸고, 공간은 사람을 바꿉니다.
            </p>
            <p className="font-kr text-base text-white/50 leading-relaxed">
              공간미디어를 통해 기술이 경험으로 녹아드는 몰입형 환경을 만듭니다.
            </p>
          </m.div>
        </m.div>
      </section>

      {/* ================================================================
          Section 3 — WHAT WE DO / BUSINESS AREAS
          ================================================================ */}
      <section
        id="what-we-do"
        className="relative min-h-screen flex items-center px-6 md:px-10 py-24 md:py-32"
        aria-labelledby="what-we-do-heading"
      >
        <div className="edge-line absolute top-0 left-0 right-0" aria-hidden="true" />
        <m.div
          initial="hidden"
          whileInView="visible"
          viewport={viewportConfig}
          variants={container}
          className="w-full max-w-4xl"
        >
          <m.div variants={item}>
            <SectionLabel>What We Do</SectionLabel>
            <SectionDivider />
          </m.div>

          <div className="border-t border-[color:var(--color-rule)]">
            {businessAreas.map((area) => (
              <m.article
                key={area.number}
                variants={item}
                className="group flex gap-6 md:gap-10 py-10 md:py-12 border-b border-[color:var(--color-rule)]"
                aria-label={area.title}
              >
                {/* Number watermark */}
                <span
                  className="font-en font-semibold text-4xl md:text-5xl text-white/10 leading-none select-none shrink-0 w-12 md:w-16 mt-1 tabular-nums"
                  aria-hidden="true"
                >
                  {area.number}
                </span>

                <div className="flex-1 min-w-0">
                  <h3 className="font-en font-semibold text-lg md:text-xl uppercase tracking-wider text-white mb-1">
                    {area.title}
                  </h3>
                  <p className="font-kr text-sm text-white/40 mb-3">
                    {area.subtitle}
                  </p>
                  <p className="font-kr text-sm text-white/50 leading-relaxed">
                    {area.description}
                  </p>
                </div>
              </m.article>
            ))}
          </div>
        </m.div>
      </section>

      {/* ================================================================
          Section 4 — VALUES
          ================================================================ */}
      <section
        id="values"
        className="relative min-h-[80vh] flex items-center px-6 md:px-10 py-24 md:py-32"
        aria-labelledby="values-heading"
      >
        <div className="edge-line absolute top-0 left-0 right-0" aria-hidden="true" />
        <m.div
          initial="hidden"
          whileInView="visible"
          viewport={viewportConfig}
          variants={container}
          className="w-full max-w-5xl"
        >
          <m.div variants={item}>
            <SectionLabel>Values</SectionLabel>
            <SectionDivider />
          </m.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
            {values.map((value) => (
              <m.article
                key={value.number}
                variants={item}
                className="relative bg-white/[0.03] border border-white/10 p-6 md:p-8 overflow-hidden"
                aria-label={value.title}
              >
                {/* Watermark number */}
                <span
                  className="absolute top-4 right-4 font-en font-bold text-5xl md:text-6xl text-white/10 leading-none select-none pointer-events-none"
                  aria-hidden="true"
                >
                  {value.number}
                </span>

                <h3 className="font-en font-bold text-lg uppercase tracking-widest text-white mb-3 relative">
                  {value.title}
                </h3>
                <p className="font-en text-sm text-white/60 leading-relaxed mb-2">
                  {value.en}
                </p>
                <p className="font-kr text-xs text-white/40 leading-relaxed">
                  {value.kr}
                </p>
              </m.article>
            ))}
          </div>
        </m.div>
      </section>

      {/* ================================================================
          Section 5 — JOURNEY / SINCE 2018
          ================================================================ */}
      <section
        id="journey"
        className="relative min-h-[60vh] flex items-center px-6 md:px-10 py-24 md:py-32"
        aria-labelledby="journey-heading"
      >
        <div className="edge-line absolute top-0 left-0 right-0" aria-hidden="true" />
        <m.div
          initial="hidden"
          whileInView="visible"
          viewport={viewportConfig}
          variants={container}
          className="w-full max-w-2xl"
        >
          <m.div variants={item}>
            <SectionLabel>Since 2018</SectionLabel>
            <SectionDivider />
          </m.div>

          {/* Timeline */}
          <ol className="relative">
            {/* Vertical connecting line */}
            <div
              className="absolute left-[3px] top-2 bottom-2 w-px bg-white/10"
              aria-hidden="true"
            />

            {milestones.map((milestone) => (
              <m.li
                key={milestone.year}
                variants={item}
                className="relative flex items-start gap-6 md:gap-10 pb-10 last:pb-0"
              >
                {/* Dot */}
                <div
                  className="relative shrink-0 w-2 h-2 rounded-full bg-white/30 mt-[6px] z-10"
                  aria-hidden="true"
                />

                <div className="flex flex-col sm:flex-row sm:items-baseline gap-1 sm:gap-6 min-w-0">
                  <span className="font-en font-bold text-xs uppercase tracking-widest text-white/30 whitespace-nowrap shrink-0">
                    {milestone.year}
                  </span>
                  <span className="font-en text-sm text-white/60 leading-relaxed">
                    {milestone.label}
                  </span>
                </div>
              </m.li>
            ))}

            {/* Current / live entry */}
            <m.li
              variants={item}
              className="relative flex items-start gap-6 md:gap-10 pb-0"
            >
              {/* Active dot */}
              <div className="relative shrink-0 mt-[6px] z-10" aria-hidden="true">
                <div className="w-2 h-2 rounded-full bg-highlight" />
                {/* Pulse ring */}
                <div className="absolute inset-0 rounded-full bg-highlight/30 animate-ping" />
              </div>

              <div className="flex flex-col sm:flex-row sm:items-baseline gap-1 sm:gap-6 min-w-0">
                <span className="font-en font-bold text-xs uppercase tracking-widest text-highlight whitespace-nowrap shrink-0">
                  2026
                </span>
                <span className="font-en text-sm text-white/60 leading-relaxed">
                  Today:{' '}
                  <SinceFoundation
                    variant="detailed"
                    className="text-white/80"
                  />
                </span>
              </div>
            </m.li>
          </ol>

          {/* Live counter badge */}
          <m.div
            variants={item}
            className="mt-12 inline-flex items-center gap-3 bg-white/[0.03] border border-white/10 px-5 py-3"
          >
            <span className="w-1.5 h-1.5 rounded-full bg-highlight animate-pulse" aria-hidden="true" />
            <SinceFoundation variant="live" className="text-white/50" />
          </m.div>
        </m.div>
      </section>

      {/* ================================================================
          Section 6 — CONTACT
          ================================================================ */}
      <section
        id="contact"
        className="relative min-h-[60vh] flex items-center px-6 md:px-10 py-24 md:py-32 bg-white/[0.02]"
        aria-labelledby="contact-heading"
      >
        <div className="edge-line absolute top-0 left-0 right-0" aria-hidden="true" />
        <m.div
          initial="hidden"
          whileInView="visible"
          viewport={viewportConfig}
          variants={container}
          className="w-full max-w-5xl"
        >
          {/* CTA headline */}
          <m.div variants={item} className="mb-14 md:mb-20">
            <SectionLabel>Contact</SectionLabel>
            <h2
              id="contact-heading"
              className="font-en font-bold text-3xl md:text-5xl uppercase tracking-tight text-white leading-tight"
            >
              LET'S CREATE
              <br />
              TOGETHER
            </h2>
          </m.div>

          {/* 2-column layout: info + form */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-12 md:gap-16">
            {/* Left column — studio info */}
            <m.div
              variants={item}
              className="space-y-8 md:col-span-1"
              aria-label="Studio information"
            >
              <div>
                <span className="font-en text-[10px] font-bold uppercase tracking-widest text-white/25 block mb-2">
                  Location
                </span>
                <p className="font-en font-bold text-sm uppercase tracking-wider text-white">
                  BUSAN, KOREA
                </p>
              </div>

              <div>
                <span className="font-en text-[10px] font-bold uppercase tracking-widest text-white/25 block mb-2">
                  Website
                </span>
                <a
                  href="https://lightive.kr"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-en font-bold text-sm uppercase tracking-wider text-white hover:text-highlight transition-colors duration-200"
                >
                  LIGHTIVE.KR
                </a>
              </div>

              <div>
                <span className="font-en text-[10px] font-bold uppercase tracking-widest text-white/25 block mb-2">
                  Founded
                </span>
                <p className="font-en text-sm text-white/50">
                  May 2018
                </p>
              </div>
            </m.div>

            {/* Right column — contact form */}
            <m.div variants={item} className="md:col-span-2">
              <ContactForm />
            </m.div>
          </div>
        </m.div>
      </section>
    </Layout>
  );
}
